import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MyLog } from '@common/common';
import { DataService } from '@data/data.service';
import { NotificationService } from '@notification/notification.service';
import { UserDeviceService } from '@services/user-device.service';
import { isJsObject } from '@util/utils';
import { tryToScrollDown } from '@util/visual-utils';
import { Subscription } from 'rxjs';
import { FillAgbDialogComponent } from '../fill-agb-dialog/fill-agb-dialog.component';
import { setByDialogHeight, tryToClear } from '../../order-dialog.helper';
import { SpecialDocumentBase } from '../../special-document-base';
import { OrderDialogService } from '../../order-dialog.service';
import { ImageEditComponent } from '../../../image-edit/image-edit/image-edit.component';
import { TextAreaEditComponent, TextAreaResult } from '../../../image-edit/text-area-edit/text-area-edit.component';
import { orderDocsErrors } from '../../order-basic';

@Component({
  selector: 'app-fill-acceptance-dialog',
  templateUrl: './fill-acceptance-dialog.component.html'
})
export class FillAcceptanceDialogComponent extends SpecialDocumentBase implements OnInit {

  readonly DAMAGE_PAGE_IDX = 1;
  readonly SIGNATURE_PAGE_IDX = 2;

  damageStyle: any = {};
  damageCanvasStyle: any = {};

  damageNoteStyle: any = {};
  damageNoteSwitchStyle: any = {};
  damageNoteClearStyle: any = {};
  damageNoteCanvasStyle: any = {};

  signCustomerStyle: any = {};
  signCustomerCanvasStyle: any = {};

  signMontageStyle: any = {};
  signMontageCanvasStyle: any = {};

  lastDamageRes?: TextAreaResult;
  lastCustomerSignature = '';
  lastWorkerSignature = '';

  //commentType = 1;
  currentPageIdx = 0;
  lastDamageData: string = '';

  signUrl = '';

  deviceSub!: Subscription;

  damageTextOptions: any = {
    maxWidth: 1.5
  };

  private _damageNote?: TextAreaEditComponent;
  @ViewChild('damageNote', {
    read: TextAreaEditComponent
  }) set damageNote(elem: TextAreaEditComponent) {
    this._damageNote = elem;
  }

  damageCanvasOptions: any = {
    penColor: '#FF0000'
  };

  private _canvasDamage?: ImageEditComponent;
  @ViewChild('canvasDamage', {
    read: ImageEditComponent
  }) set canvasDamage(elem: ImageEditComponent) {
    this._canvasDamage = elem;
  }

  private _signatureCanvasCustomer?: ImageEditComponent;
  @ViewChild('signCanvasCustomer', {
    read: ImageEditComponent
  }) set signPadCustomer(elem: ImageEditComponent) {
    this._signatureCanvasCustomer = elem;
  }

  private _signMontage?: ImageEditComponent;
  @ViewChild('signCanvas', {
    read: ImageEditComponent
  }) set signPad(elem: ImageEditComponent) {
    this._signMontage = elem;
  }

  private _cnt?: ElementRef;
  @ViewChild('cnt') set contentRef(content: ElementRef) {
    this._cnt = content;
    setByDialogHeight(this._cnt?.nativeElement as HTMLElement);
  }

  constructor(
    public dialogRef: MatDialogRef<FillAgbDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public dialogData: any,
    notificationService: NotificationService,
    private dataService: DataService,
    private userDeviceService: UserDeviceService,
    dialogService: OrderDialogService
  ) {
    super(notificationService);
    this.setDefaultPositions(dialogService);
  }

  setDefaultPositions(dialogService: OrderDialogService) {

    const dialogStyle = dialogService.getStylesForDialog('acceptance', {});

    if (!isJsObject(dialogStyle)) {
      MyLog.error('Failed to get style');
      return;
    }

    let style = dialogStyle.damage;

    if (style) {
      this.damageStyle = style?.pos || this.damageStyle;
      this.damageCanvasStyle = style?.canvas || this.damageCanvasStyle;
    }

    style = dialogStyle.damageNote;
    if (style) {
      this.damageNoteStyle = style?.pos || this.damageNoteStyle;
      this.damageNoteSwitchStyle = style?.switch || this.damageNoteSwitchStyle;
      this.damageNoteClearStyle = style?.clearPos || this.damageNoteClearStyle;
      this.damageNoteCanvasStyle = style?.canvas || this.damageNoteCanvasStyle;
    }
    //line-height: 1.06cm
    this.damageNoteCanvasStyle.lineHeight = '1.06cm';

    style = dialogStyle.customerSignature;
    if (style) {
      this.signCustomerStyle = style?.pos || this.signCustomerStyle;
      this.signCustomerCanvasStyle = style?.canvas || this.signCustomerCanvasStyle;
    }

    style = dialogStyle.montageSignature;
    if (style) {
      this.signMontageStyle = style?.pos || this.signMontageStyle;
      this.signMontageCanvasStyle = style?.canvas || this.signMontageCanvasStyle;
    }
  }

  ngOnInit(): void {
    const prefix = '/montage/documents/' + this.dialogData.orderId;
    this.signUrl = prefix + '/acceptance/sign';
    this.editUrl = prefix + '/acceptance/edit';

    this.deviceSub = this.userDeviceService.change().subscribe((ev: any) => {

      const toResize: any[] = [
        this._canvasDamage,
        this._damageNote,
        this._signatureCanvasCustomer,
        this._signMontage
      ];

      for (let e of toResize) {
        e && e.resizeCanvas();
      }

      setByDialogHeight(this._cnt?.nativeElement as HTMLElement);
    });

    this.requestEditData(
      this.editUrl,
      this.dataService,
      this.notificationService,
      'Problem beim Laden der Daten für Abnahmeprotokoll'
    );
  }

  ngOnDestroy(): void {
    this.deviceSub.unsubscribe();
  }

  ngAfterViewInit(): void {
    tryToScrollDown(this._cnt?.nativeElement);
  }

  loadPage(pageNumber: Event| number) {
    //console.log('Load page', pageNumber);
    if (this.currentPageIdx !== pageNumber) {
      this.storeCurrentData();
      this.updatePageData(pageNumber as number);
    }
    this.currentPageIdx = pageNumber as number;
  }

  storeCurrentData() {
    const errors: any[] = []
    let error: any,
      sign: any;

    switch (this.currentPageIdx) {
      case this.DAMAGE_PAGE_IDX:
        this.lastDamageData = '';
        sign = this._canvasDamage;
        error = orderDocsErrors.DAMAGE;

        if (sign) {
          this.lastDamageData = '';
          const res = sign.toDataURL();
          if (null !== res) {
            error = '';
            this.lastDamageData = res;
          }
        }
        error && errors.push(error);

        const damageRes = this.getDamageText();
        this.lastDamageRes = damageRes;

        damageRes.error && errors.push(damageRes.error);
        break;

      case this.SIGNATURE_PAGE_IDX:
        error = orderDocsErrors.CUSTOMER_SIGN;
        sign = this._signatureCanvasCustomer;

        if (sign) {
          this.lastCustomerSignature = '';
          const res = sign.toDataURL();
          if (null !== res) {
            error = '';
            this.lastCustomerSignature = res;
          }
        }
        error && errors.push(error);

        sign = this._signMontage;
        error = orderDocsErrors.MONTAGE_SIGN;

        if (sign) {
          this.lastWorkerSignature = '';
          const res = sign.toDataURL();
          if (null !== res) {
            error = '';
            this.lastWorkerSignature = res;
          }
        }
        error && errors.push(error);
        break;
    }

    errors.length > 0 && this.notificationService.showErrors(errors);
  }

  loadPageSetData(pageIndex: number) {
    //MyLog.log('Page data:', this.currentPage);
    if (this.currentPage?.data) {
      //MyLog.log('DATA:len:', this.currentPage.data?.length);
      this.imgUrl = 'data:image/png;base64,' + this.currentPage.data;
    }
  }

  getDamageText(): TextAreaResult {

    let res: TextAreaResult = {
      hasError: true,
      type: 1,
      error: orderDocsErrors.DEFECTS
    };

    if (this._damageNote) {
      res = this._damageNote.getDamageText();
      if (res.hasError) {
        res.error = orderDocsErrors.DEFECTS;
      }
    }

    return res;
  }


  prepareRequest() {
    const errors: any[] = [];
    const reqData: any = {
      //commentType: this.commentType
    };

    this.storeCurrentData();

    if (this.lastDamageRes) {
      const damageRes = this.lastDamageRes;

      if (damageRes.error) {
        errors.push(damageRes.error);
      }

      if (damageRes.type === 1 && damageRes.text) {
        reqData.damage_text = damageRes.text;
      }

      if (damageRes.type === 2 && damageRes.free_text) {
        reqData.damage_text_free = damageRes.free_text;
      }
    }

    if (this.lastDamageData) {
      reqData.damage = this.lastDamageData;
    }

    if (this.lastCustomerSignature) {
      reqData.customer_signature = this.lastCustomerSignature;
    } else {
      errors.push('Unterschrift des Kunden nicht gesetzt');
    }

    if (this.lastWorkerSignature) {
      reqData.montage_signature = this.lastWorkerSignature;
    } else {
      errors.push('Unterschrift des Montageleiters nicht gesetzt');
    }

    return {
      data: reqData,
      errors
    };
  }

  signClose(save: boolean) {

    if (save) {

      const req = this.prepareRequest();

      if (req.errors.length) {
        this.notificationService.showErrors(req.errors);
      } else {
        this.dataService.post(this.signUrl, req.data, {}).subscribe((res: any) => {
          const data = res?.result;
          if (data.success) {
            this.notificationService.showSuccess(
              'Annahmeprotokoll gespeichert'
            );
            this.dialogRef.close(data);
          } else {
            let errors: any[] = [];
            if (Array.isArray(data?.errors)) {
              errors = data.errors;
            } else {
              errors.push(
                data?.error || 'Unbekannter Fehler beim Speichern'
              );
            }

            this.notificationService.showErrors(errors);
          }
        });
      }

    } else {
      this.dialogRef.close(null);
    }
  }

  getEditComponents(): any {
    const res: any = {};

    res[this.CUSTOMER] = this._signatureCanvasCustomer;
    res[this.DAMAGE] = this._canvasDamage;
    res[this.DAMAGE_NOTE] = this._damageNote;
    res[this.MONTAGE] = this._signMontage;

    return res;
  }

  clear(type: any) {
    if (type) {
      let t = this.getEditComponents();
      tryToClear(t[type]);
    }
  }

  readonly CUSTOMER = 'customer_sign';
  readonly DAMAGE = 'damage';
  readonly DAMAGE_NOTE = 'damage_note';
  readonly MONTAGE = 'montage_sign';
}
